import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const statusColors = {
  Active: 'success',
  Beta: 'warning',
  Planned: 'default',
  Alpha: 'info',
};

const projects = {
  github: [
    {
      icon: <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'OpenEye - OpenCV Home Security',
      status: 'Active',
      description:
        'An alternative to motionEye that utilizes OpenCV to add additional features and functionality. Built for home security enthusiasts who want more control over their surveillance system with advanced computer vision capabilities.',
      tags: ['Python', 'OpenCV', 'Computer Vision', 'MIT License'],
      links: [
        { label: 'View on GitHub', url: 'https://github.com/M1K31/OpenEye-OpenCV_Home_Security', primary: true },
        { label: 'Docker Hub', url: 'https://hub.docker.com/r/im1k31s/openeye-opencv_home_security', primary: false },
      ],
    },
    {
      icon: <CodeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'MagicMirror-Custom',
      status: 'Active',
      description:
        'A customized build of the popular MagicMirror\u00B2 smart mirror platform. Features personalized modules and configurations for an enhanced smart home display experience.',
      tags: ['JavaScript', 'Node.js', 'Raspberry Pi', 'Smart Home'],
      links: [
        { label: 'View on GitHub', url: 'https://github.com/M1K31/MagicMirror-Custom', primary: true },
      ],
    },
  ],
  docker: [
    {
      icon: <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'openeye-opencv_home_security',
      status: 'Active',
      description:
        'Containerized version of OpenEye Home Security system. Deploy your own OpenCV-powered surveillance solution with Docker for easy setup and portability across different hardware platforms.',
      tags: ['Python', 'OpenCV', 'Home Security', 'Docker'],
      pullCommand: 'docker pull im1k31s/openeye-opencv_home_security',
      links: [
        { label: 'View on Docker Hub', url: 'https://hub.docker.com/r/im1k31s/openeye-opencv_home_security', primary: true },
      ],
    },
  ],
};

const ProjectCard = ({ project, onCopy }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
    }}
  >
    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        {project.icon}
        <Box>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {project.title}
          </Typography>
          <Chip
            label={project.status}
            size="small"
            color={statusColors[project.status] || 'default'}
            sx={{ mt: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, lineHeight: 1.7 }}>
        {project.description}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
        {project.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {project.links.map((link) => (
          <Button
            key={link.label}
            variant={link.primary ? 'contained' : 'outlined'}
            size="small"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            {link.label} &rarr;
          </Button>
        ))}
        {project.pullCommand && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => onCopy(project.pullCommand)}
            sx={{ fontWeight: 600 }}
          >
            Copy Pull Command
          </Button>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ToolsDevelopment = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ open: true, message: `Copied: ${text}` });
    }).catch(() => {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbar({ open: true, message: `Copied: ${text}` });
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
          Tools Under Development
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Explore our open-source projects and tools currently in development. We believe in building
          in public and welcome community contributions.
        </Typography>
      </Box>

      {/* GitHub Projects */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, pb: 1.5, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <GitHubIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            GitHub Projects
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {projects.github.map((project) => (
            <Grid item xs={12} md={6} key={project.title}>
              <ProjectCard project={project} onCopy={handleCopy} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Docker Hub */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, pb: 1.5, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Docker Hub Images
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {projects.docker.map((project) => (
            <Grid item xs={12} md={6} key={project.title}>
              <ProjectCard project={project} onCopy={handleCopy} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contribute Section */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #0d3b66 0%, #082844 100%)',
          color: 'white',
          p: 5,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 600, mb: 1.5 }}>
          Want to Contribute?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
          We welcome contributions from the community! Whether it's bug fixes, new features, or
          documentation improvements, your help makes these tools better for everyone.
        </Typography>
        <Button
          variant="contained"
          size="large"
          href="https://github.com/M1K31"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<GitHubIcon />}
          sx={{
            backgroundColor: 'secondary.main',
            color: '#0d1b2a',
            fontWeight: 700,
            px: 4,
            '&:hover': { backgroundColor: 'secondary.dark' },
          }}
        >
          Visit GitHub Profile
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ToolsDevelopment;
